// ============================================================
// Imported history → daily rollups (source: import)
//
// Daily aggregates from another tool (GA4's BigQuery export via
// scripts/ga4-import) are written into rollup_daily_events / _dims, the
// tables reports read beyond the Analytics Engine window, and every
// imported (project, day) is recorded in rollup_imports. Rules:
//   - only days BEFORE the project's first native rollup day, and at least
//     two days old: imported and native data never mix on a day
//   - re-importing a day replaces it (idempotent); rollupDay() never drops it
//   - retention purges imported days like native ones
// ============================================================
import { EVENT_NAME_RE } from './config.js';
import { addDays } from './query/sql.js';
import { ROLLUP_DIMS } from './rollup.js';
import { CUSTOM_METRIC_SLOTS } from './schema.js';
export const IMPORT_MAX_DAYS_PER_REQUEST = 31;
export const IMPORT_MAX_EVENT_ROWS_PER_DAY = 1_000;
export const IMPORT_MAX_DIM_ROWS_PER_DAY = 10_000;
export const IMPORT_ORIGINS = ['ga4', 'other'];
export class ImportInputError extends Error {
}
const DAY_RE = /^\d{4}-\d{2}-\d{2}$/;
const SUB_TENANT_RE = /^[A-Za-z0-9_.:-]{0,64}$/;
function num(v, field) {
    const x = v === undefined || v === null ? 0 : Number(v);
    if (!Number.isFinite(x) || x < 0) {
        throw new ImportInputError(`${field} must be a non-negative number`);
    }
    return x;
}
function dimColumn(dim, config) {
    if (typeof dim === 'string' && dim.startsWith('d:')) {
        const slot = config.dimensions.find(d => d.name === dim.slice(2))?.slot;
        if (slot) {
            return `d${slot}`;
        }
    }
    if (typeof dim === 'string' && ROLLUP_DIMS.includes(dim) && !/^d\d$/.test(dim)) {
        return dim;
    }
    throw new ImportInputError(`dimension '${String(dim)}' cannot be imported`);
}
/** Validate a request body `{ origin, days: [{ day, sub_tenant?, events: [...], dims: [...] }] }`. */
export function parseImportBody(raw, config) {
    const origin = (raw.origin ?? 'ga4');
    if (!IMPORT_ORIGINS.includes(origin)) {
        throw new ImportInputError(`origin must be ${IMPORT_ORIGINS.join(' | ')}`);
    }
    if (!Array.isArray(raw.days) ||
        raw.days.length === 0 ||
        raw.days.length > IMPORT_MAX_DAYS_PER_REQUEST) {
        throw new ImportInputError(`days must be an array of 1…${IMPORT_MAX_DAYS_PER_REQUEST}`);
    }
    const seen = new Set();
    const days = raw.days.map((d, i) => {
        const o = (d ?? {});
        if (typeof o.day !== 'string' || !DAY_RE.test(o.day) || Number.isNaN(Date.parse(o.day))) {
            throw new ImportInputError(`days[${i}].day must be YYYY-MM-DD`);
        }
        const subTenant = o.sub_tenant === undefined || o.sub_tenant === null ? '' : String(o.sub_tenant);
        if (!SUB_TENANT_RE.test(subTenant)) {
            throw new ImportInputError(`days[${i}].sub_tenant is invalid`);
        }
        if (seen.has(`${o.day}|${subTenant}`)) {
            throw new ImportInputError(`days[${i}] repeats ${o.day}`);
        }
        seen.add(`${o.day}|${subTenant}`);
        const events = Array.isArray(o.events) ? o.events : [];
        const dims = Array.isArray(o.dims) ? o.dims : [];
        if (events.length > IMPORT_MAX_EVENT_ROWS_PER_DAY ||
            dims.length > IMPORT_MAX_DIM_ROWS_PER_DAY) {
            throw new ImportInputError(`days[${i}]: at most ${IMPORT_MAX_EVENT_ROWS_PER_DAY} event rows and ${IMPORT_MAX_DIM_ROWS_PER_DAY} dim rows`);
        }
        const ev = (e, where) => {
            if (typeof e !== 'string' || !EVENT_NAME_RE.test(e)) {
                throw new ImportInputError(`${where}.event must match ${EVENT_NAME_RE}`);
            }
            return e;
        };
        return {
            day: o.day,
            subTenant,
            events: events.map((r, j) => {
                const x = (r ?? {});
                const w = `days[${i}].events[${j}]`;
                return {
                    event: ev(x.event, w),
                    count: num(x.count, `${w}.count`),
                    value: num(x.value, `${w}.value`),
                    qty: num(x.qty, `${w}.qty`),
                };
            }),
            dims: dims.map((r, j) => {
                const x = (r ?? {});
                const w = `days[${i}].dims[${j}]`;
                const key = typeof x.key === 'string' ? x.key.slice(0, 512) : '';
                if (!key) {
                    throw new ImportInputError(`${w}.key is required`);
                }
                return {
                    event: ev(x.event, w),
                    dim: dimColumn(x.dim, config),
                    key,
                    count: num(x.count, `${w}.count`),
                    value: num(x.value, `${w}.value`),
                };
            }),
        };
    });
    return { origin, days };
}
/**
 * Days strictly before this are importable: the first day with native rollups
 * (rows not marked as imported), capped at two days ago.
 */
export async function importCutoff(db, projectId, now = new Date()) {
    const row = await db
        .prepare(`SELECT MIN(day) AS day FROM rollup_daily_events e WHERE project_id = ?
               AND NOT EXISTS (SELECT 1 FROM rollup_imports i WHERE i.project_id = e.project_id AND i.day = e.day)`)
        .bind(projectId)
        .first();
    const cap = addDays(now.toISOString().slice(0, 10), -1);
    return row?.day && row.day < cap ? row.day : cap;
}
const SUM_COLS = [
    'value',
    'qty',
    'duration_ms',
    ...Array.from({ length: CUSTOM_METRIC_SLOTS }, (_, i) => `m${i + 1}`),
];
/**
 * Replace the imported rows of each (project, sub-tenant, day) in `days`; other sub-tenants'
 * rows for the same day are kept. All days in one request share an import id.
 */
export async function writeImportDays(db, projectId, origin, importId, days) {
    // Several sub-tenants may share a day: group, then sum duplicate keys.
    const byDay = new Map();
    for (const d of days) {
        byDay.set(d.day, [...(byDay.get(d.day) ?? []), d]);
    }
    let eventRows = 0;
    let dimRows = 0;
    for (const [day, parts] of byDay) {
        const ev = new Map();
        const dm = new Map();
        for (const p of parts) {
            for (const r of p.events) {
                const k = `${p.subTenant}\u0000${r.event}`;
                const cur = ev.get(k) ?? {
                    st: p.subTenant,
                    event: r.event,
                    count: 0,
                    value: 0,
                    qty: 0,
                };
                cur.count += r.count;
                cur.value += r.value;
                cur.qty += r.qty;
                ev.set(k, cur);
            }
            for (const r of p.dims) {
                const k = `${p.subTenant}\u0000${r.event}\u0000${r.dim}\u0000${r.key}`;
                const cur = dm.get(k) ?? {
                    st: p.subTenant,
                    event: r.event,
                    dim: r.dim,
                    key: r.key,
                    count: 0,
                    value: 0,
                };
                cur.count += r.count;
                cur.value += r.value;
                dm.set(k, cur);
            }
        }
        const zeros = SUM_COLS.filter(c => c !== 'value' && c !== 'qty');
        const evStmt = db.prepare(`INSERT INTO rollup_daily_events (project_id, sub_tenant, day, event, count, value, qty, ${zeros.join(', ')})
             VALUES (?, ?, ?, ?, ?, ?, ?, ${zeros.map(() => '0').join(', ')})`);
        const dimStmt = db.prepare('INSERT INTO rollup_daily_dims (project_id, sub_tenant, day, event, dim, key, count, value) VALUES (?, ?, ?, ?, ?, ?, ?, ?)');
        // Replace only the sub-tenants this import carries: imports run once per source
        // (e.g. one GA4 property per sub-tenant) and must not erase each other's days.
        const scopes = [...new Set(parts.map(p => p.subTenant))];
        const stmts = [
            ...scopes.flatMap(st => [
                db
                    .prepare('DELETE FROM rollup_daily_events WHERE project_id = ? AND day = ? AND sub_tenant = ?')
                    .bind(projectId, day, st),
                db
                    .prepare('DELETE FROM rollup_daily_dims WHERE project_id = ? AND day = ? AND sub_tenant = ?')
                    .bind(projectId, day, st),
            ]),
            ...[...ev.values()].map(r => evStmt.bind(projectId, r.st, day, r.event, r.count, r.value, r.qty)),
            ...[...dm.values()].map(r => dimStmt.bind(projectId, r.st, day, r.event, r.dim, r.key, r.count, r.value)),
            db
                .prepare(`INSERT INTO rollup_imports (project_id, day, source, origin, import_id, event_rows, dim_rows, created_at)
                     VALUES (?, ?, 'import', ?, ?, ?, ?, ?)
                     ON CONFLICT (project_id, day) DO UPDATE SET origin = excluded.origin, import_id = excluded.import_id,
                       event_rows = excluded.event_rows, dim_rows = excluded.dim_rows, created_at = excluded.created_at`)
                .bind(projectId, day, origin, importId, ev.size, dm.size, Date.now()),
        ];
        // Typically one batch per day (delete + inserts land together); a re-import repairs a torn day.
        for (let i = 0; i < stmts.length; i += 1000) {
            await db.batch(stmts.slice(i, i + 1000));
        }
        eventRows += ev.size;
        dimRows += dm.size;
    }
    return { days: byDay.size, event_rows: eventRows, dim_rows: dimRows };
}
/** Remove imported days in [from, to]. Native days are never touched. */
export async function deleteImportDays(db, projectId, from, to) {
    const { results } = await db
        .prepare('SELECT day FROM rollup_imports WHERE project_id = ? AND day >= ? AND day <= ?')
        .bind(projectId, from, to)
        .all();
    const days = (results ?? []).map(r => r.day);
    for (const day of days) {
        await db.batch([
            db
                .prepare('DELETE FROM rollup_daily_events WHERE project_id = ? AND day = ?')
                .bind(projectId, day),
            db
                .prepare('DELETE FROM rollup_daily_dims WHERE project_id = ? AND day = ?')
                .bind(projectId, day),
            db
                .prepare('DELETE FROM rollup_imports WHERE project_id = ? AND day = ?')
                .bind(projectId, day),
        ]);
    }
    return days.length;
}
//# sourceMappingURL=imports.js.map